import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  onReset?: () => void;
  children: React.ReactNode;
}

export function FilterDrawer({
  open,
  onClose,
  onApply,
  onReset,
  children,
}: FilterDrawerProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      // Small delay to ensure smooth animation
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        document.body.style.overflow = "unset";
      }, 300);
      return () => {
        clearTimeout(timer);
        document.body.style.overflow = "unset";
      };
    }
  }, [open]);

  // Reset scroll position when drawer opens
  useEffect(() => {
    if (open && isVisible) {
      const scrollContainer = document.querySelector(".filter-drawer-content");
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }
  }, [open, isVisible]);

  if (!open && !isVisible) return null;

  return (
    <>
      {/* Backdrop with fade animation */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/80 transition-all duration-300",
          isVisible && open ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer with slide animation matching sheet variants */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 h-full w-full sm:w-3/4 border-r bg-background shadow-lg sm:max-w-sm",
          "transition-all ease-in-out",
          isVisible && open
            ? "translate-x-0 duration-500"
            : "-translate-x-full duration-300",
        )}
        role="dialog"
        aria-modal="true"
        aria-label={t("booking.shows.filters.filterOptions")}
      >
        <div className="h-full flex flex-col">
          {/* Filter content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden filter-drawer-content">
            {children}
          </div>

          {/* Footer with action buttons */}
          <div className="sticky bottom-0 left-0 right-0 w-full border-t bg-background p-4 shadow-lg">
            <div className="flex gap-3">
              <Button onClick={onApply} className="flex-1" size="default">
                {t("booking.shows.filters.apply")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
